import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ClearancePopup from './components/ClearancePopup';
import FaviconManager from './components/FaviconManager';
import ErrorBoundary from './components/ErrorBoundary';
import { installPhoneTracking } from './services/googleAds';
import { AuthProvider } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import HomePage from './pages/HomePage';

// Eagerly load public pages (react-snap needs them for pre-rendering)
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ConfiguratorPage from './pages/ConfiguratorPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import QuotePage from './pages/QuotePage';
import PrivacyPage from './pages/PrivacyPage';
import UnsubscribePage from './pages/UnsubscribePage';
import TermsPage from './pages/TermsPage';
import ComparePage from './pages/ComparePage';
import AwningsPage from './pages/AwningsPage';
import ColoursPage from './pages/ColoursPage';
import GalleryPage from './pages/GalleryPage';
import MeasuringGuidePage from './pages/MeasuringGuidePage';
import ForInstallersPage from './pages/ForInstallersPage';
import DeliveryPage from './pages/DeliveryPage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';

// Customer auth pages are pre-rendered too (noindex, but real routes), and
// they are tiny, so they load eagerly with the rest of the public site.
import SignInPage from './pages/auth/SignInPage';
import SignUpPage from './pages/auth/SignUpPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import AccountPage from './pages/auth/AccountPage';

// Lazy-loaded routes: the admin (not pre-rendered, never hydrated) and the
// 404 page. These are the only routes that sit inside a Suspense boundary.
// A boundary around a pre-rendered route makes React look for a Suspense
// marker in the HTML that a captured DOM never has, so hydration fails
// (#418) and the page is rebuilt on the client - which is what used to
// happen on every public page while <Suspense> wrapped the whole <Routes>.
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminSetup = lazy(() => import('./pages/admin/AdminSetup'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const lazyFallback = <div style={{ minHeight: '60vh' }} />;
const withSuspense = (element) => <Suspense fallback={lazyFallback}>{element}</Suspense>;

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  // CQ-16: was firing window.scrollTo(0, 0) during render, so every
  // re-render of the layout (state change anywhere up the tree) reset
  // scroll. Now bound to the pathname so it only fires on route change.
  //
  // Hash-aware: a link like /products/awnings#colours (the main nav's
  // "Colours" item) would otherwise be yanked back to the top by the
  // scroll-to-top below. Target sections are often data-driven and not mounted
  // on the first frame, so retry briefly before giving up.
  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      let frame;
      let timer;
      let tries = 0;
      let settleTries = 0;
      let lastTop = null;

      // Images above the target load after the first paint and push it further
      // down the page, so a single scroll lands short. Re-scroll until the
      // target's position stops moving (or we run out of patience).
      const settle = (el) => {
        el.scrollIntoView();
        const top = Math.round(el.getBoundingClientRect().top);
        if (top === lastTop || settleTries++ > 20) return;
        lastTop = top;
        timer = setTimeout(() => settle(el), 100);
      };

      const seek = () => {
        const el = document.getElementById(id);
        if (el) { settle(el); return; }
        if (tries++ < 30) frame = requestAnimationFrame(seek);
        else window.scrollTo(0, 0);
      };
      seek();
      return () => {
        if (frame) cancelAnimationFrame(frame);
        if (timer) clearTimeout(timer);
      };
    }
    window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash]);
  return null;
}

function Layout() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  // Google Ads: one listener for every tel: link on the site, installed once
  // for the life of the app (Layout never unmounts).
  useEffect(() => installPhoneTracking(), []);

  return (
    <>
      <ScrollToTop />
      <FaviconManager />
      {!isAdmin && <Navbar />}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/compare" element={<ComparePage />} />
          <Route path="/colours" element={<ColoursPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/measuring-guide" element={<MeasuringGuidePage />} />
          <Route path="/for-installers" element={<ForInstallersPage />} />
          <Route path="/products/awnings" element={<AwningsPage />} />
          <Route path="/delivery" element={<DeliveryPage />} />
          <Route path="/products/:slug" element={<ProductDetailPage />} />
          <Route path="/configure/:slug" element={<ConfiguratorPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/request-quote" element={<QuotePage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/admin" element={withSuspense(<AdminDashboard />)} />
          <Route path="/admin/login" element={withSuspense(<AdminLogin />)} />
          <Route path="/admin/setup" element={withSuspense(<AdminSetup />)} />
          <Route path="*" element={withSuspense(<NotFoundPage />)} />
        </Routes>
      {!isAdmin && <Footer />}
      {!isAdmin && <ClearancePopup />}
    </>
  );
}

function App() {
  // LOG-18: ErrorBoundary at the top of the tree catches unhandled
  // exceptions in any descendant and shows a friendly fallback instead
  // of the React-default blank white screen.
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <SettingsProvider>
              <Layout />
            </SettingsProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
